/**
 * i18n mínimo e tipado. O pt-BR define as chaves; outros idiomas podem ser
 * parciais (o que faltar cai no pt-BR). Funciona dentro e fora do React.
 */
import { useSyncExternalStore } from "react";
import { en } from "./en";
import { ptBR } from "./pt-BR";

export type Dict = typeof ptBR;
export type Lang = "pt-BR" | "en";

type DeepPartial<T> = { [K in keyof T]?: T[K] extends string | string[] ? T[K] : DeepPartial<T[K]> };
export type PartialDict = DeepPartial<Dict>;

/** Chaves cujo valor é texto ("auth.login.title"). */
type StringPaths<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : T[K] extends string[] ? never : StringPaths<T[K], `${P}${K}.`>;
}[keyof T & string];
/** Chaves cujo valor é lista de textos. */
type ListPaths<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? never : T[K] extends string[] ? `${P}${K}` : ListPaths<T[K], `${P}${K}.`>;
}[keyof T & string];

export type TKey = StringPaths<Dict>;
export type TListKey = ListPaths<Dict>;
export type TVars = Record<string, string | number>;

const DICTS: Record<Lang, PartialDict> = { "pt-BR": ptBR, en };
const STORAGE_KEY = "tenanci.lang";

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "pt-BR") return saved;
  } catch {
    /* localStorage indisponível */
  }
  // sem escolha salva: o idioma do navegador (português → pt-BR; qualquer outro → en)
  const nav = typeof navigator === "undefined" ? "pt" : (navigator.language ?? "pt").toLowerCase();
  return nav.startsWith("pt") ? "pt-BR" : "en";
}

let current: Lang = typeof window === "undefined" ? "pt-BR" : initialLang();
const listeners = new Set<() => void>();

function lookup(dict: PartialDict, key: string): unknown {
  let node: unknown = dict;
  for (const part of key.split(".")) {
    if (node && typeof node === "object" && part in (node as Record<string, unknown>)) {
      node = (node as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return node;
}

function interpolate(text: string, vars?: TVars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

/** Traduz uma chave (com fallback para pt-BR e, em último caso, a própria chave). */
export function t(key: TKey, vars?: TVars): string {
  const value = lookup(DICTS[current], key) ?? lookup(ptBR, key);
  return typeof value === "string" ? interpolate(value, vars) : key;
}

/** Lista de textos (ex.: recursos de um plano). */
export function tList(key: TListKey): string[] {
  const value = lookup(DICTS[current], key) ?? lookup(ptBR, key);
  return Array.isArray(value) ? (value as string[]) : [];
}

/** Verifica se existe tradução para uma chave dinâmica (ex.: códigos de erro da API). */
export function hasKey(key: string): key is TKey {
  return typeof lookup(ptBR, key) === "string";
}

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang) {
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignora */
  }
  document.documentElement.lang = lang;
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Hook: re-renderiza o componente quando o idioma muda. */
export function useI18n() {
  const lang = useSyncExternalStore(subscribe, getLang, getLang);
  return { t, tList, lang, setLang };
}

type DeepPartialAny<T> = T extends string | number | boolean | ((...a: never[]) => unknown) | unknown[] ? T : { [K in keyof T]?: DeepPartialAny<T[K]> };

const isPlain = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * Textos de uma função nos dois idiomas (arquivos strings.ts das telas): `export const X = localized(pt, en)` e o
 * resto do código continua lendo `X.title`. Cada leitura devolve o idioma atual; o que faltar no en cai no pt-BR.
 * Trocar o idioma remonta o painel (main.tsx), então os textos lidos na renderização acompanham.
 */
export function localized<T extends object>(pt: T, en: DeepPartialAny<T>): T {
  const wrap = (p: Record<string, unknown>, e: Record<string, unknown> | undefined): unknown =>
    new Proxy(p, {
      get(target, key) {
        const base = Reflect.get(target, key);
        const other = current === "en" && e ? e[key as string] : undefined;
        if (isPlain(base)) return wrap(base, isPlain(other) ? other : undefined);
        return other ?? base;
      },
    });
  return wrap(pt as Record<string, unknown>, en as Record<string, unknown>) as T;
}

/** Locale para Intl (datas e moedas). */
export const intlLocale = () => (current === "en" ? "en-US" : "pt-BR");
